// AxiosService.ts

import axios, { AxiosRequestConfig, AxiosResponse } from "axios";

/**
 * リクエスト用の型宣言
 */
type RequestOptions = {
  params?: Record<string, any>; // クエリパラメータ
  data?: Record<string, any>; // リクエストボディ
  headers?: Record<string, any>; // ヘッダー情報
  responseType?: AxiosRequestConfig["responseType"];
};

const csrfExcludedPaths = [
  "/auth/set-cookie",
  "/auth/callback",
];

let csrfToken: string | null = null; // キャッシュ用変数
let logoutHandler: (() => void) | null = null; // ログイン画面への遷移処理
/**
 * ログイン後にCSRFトークンを取得し、保存する
 */
export async function fetchCsrfToken(): Promise<void> {
  try {
    const response = await axios.get<{ token: string }>("/csrf-token", {
      baseURL: process.env.REACT_APP_BASE_URL,
      withCredentials: true,
    });
    csrfToken = response.data.token;
  } catch (error) {
    console.warn("CSRFトークンの取得に失敗しましたが、リクエストは継続します", error);
  }
}

/**
 * ログアウト時に呼び出すハンドラーを登録する
 * @param handler - ログイン画面へ遷移する処理
 */
export const setLogoutHandler = (handler: () => void) => {
  logoutHandler = handler;
};

// レスポンスへインターセプターを設定
axios.interceptors.response.use(

  (response: AxiosResponse) => {
    // レスポンス正常時の共通処理
    return response;
  },
  (error: unknown) => {
    // 認証エラー時の処理
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 401) {
        // ログイン処理中の画面（/login・/callback）では401でも何もしない。
        const isAuthInProgress =
          window.location.pathname === "/login" ||
          window.location.pathname === "/callback";
        if (logoutHandler && !isAuthInProgress) {
          logoutHandler();
        }
      }
      // 権限不足時のエラー処理
      else if (error.response?.status === 403) {
        alert("ユーザーはこの操作を実行する権限がありません。");
      }
      // その他エラー発生時の処理
      else {
        // バックエンドでエラーメッセージを設定した際にエラーメッセージを表示、なければデフォルトメッセージ
        const errorMessage = error.response?.data?.message || "予期しないエラーが発生しました";
        alert(errorMessage);
      }
    } else {
      console.log(error);
    }
    return Promise.reject(error);
  }

);

/**
 * サーバー通信を行う共通メソッド
 * @param url 必須：サーバー側へ接続するためのurl
 * @param method 必須：HTTPメソッド
 * @param options 任意：リクエストオプション
 */
export async function axiosRequest<T>(
  url: string,
  method: "GET" | "POST" | "PUT" | "DELETE",
  options?: RequestOptions
): Promise<T> {
  const { params, data, headers = {}, responseType } = options || {};
  const shouldExcludeCsrf = csrfExcludedPaths.some(path => url.includes(path));

  // GET以外のリクエストでCSRFトークンを付与
  if (method !== "GET" && !shouldExcludeCsrf) {
    if (!csrfToken) {
      await fetchCsrfToken();
    }
    headers["X-CSRF-TOKEN"] = csrfToken;
  }
  const config: AxiosRequestConfig = {
    url,
    baseURL: process.env.REACT_APP_BASE_URL,
    method,
    params,
    data,
    headers,
    withCredentials: true,
    responseType,
  };

  try {
    const response: AxiosResponse<T> = await axios(config);
    return response.data;
  } catch (error) {
    throw error;
  }
}
